"use client"

import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Menu, X, User, LayoutDashboard, Gift, ChevronDown, Settings, LogOut } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

import { WalletButton } from "@/components/wallet-button"

import { useState, useEffect } from "react"
import { useWallet } from "@/lib/wallet-context"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface NavItem {
  href?: string
  label: string
  items?: { href: string; label: string }[]
}

function NavDropdown({ label, items }: { label: string; items: { href: string; label: string }[] }) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <motion.button
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-md"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {label}
        <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 15, scale: 0.9, rotateX: -15 }}
            animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
            exit={{ opacity: 0, y: 15, scale: 0.9, rotateX: -15 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 dark:bg-[#0a0a0f] bg-white border dark:border-white/10 border-black/5 rounded-2xl shadow-2xl overflow-hidden py-2 z-[60] perspective-1000"
          >
            {items.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="block px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-primary/10 transition-all rounded-lg mx-2"
              >
                {item.label}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function NetworkDropdown({ isTestnet, onSwitch }: { isTestnet: boolean; onSwitch: (targetIsTestnet: boolean) => void }) {
  const [isOpen, setIsOpen] = useState(false)

  const activeColor = isTestnet ? '#f59e0b' : '#22c55e'
  const activeLabel = isTestnet ? 'Testnet' : 'Mainnet'
  const activeBg = isTestnet ? 'rgba(251,191,36,0.08)' : 'rgba(74,222,128,0.08)'
  const activeBorder = isTestnet ? 'rgba(251,191,36,0.3)' : 'rgba(74,222,128,0.3)'

  return (
    <div
      className="relative hidden xl:block"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {/* Current network badge (trigger) */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 20 }}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold cursor-pointer select-none"
        style={{
          borderColor: activeBorder,
          backgroundColor: activeBg,
          color: activeColor,
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <motion.span
          className="w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: activeColor }}
          animate={{ scale: [1, 1.4, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
        {activeLabel}
        <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </motion.div>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 350 }}
            className="absolute top-full right-0 mt-2 w-44 dark:bg-[#0a0a0f] bg-white border dark:border-white/10 border-black/5 rounded-2xl shadow-2xl overflow-hidden py-1.5 z-[60]"
          >
            {/* Mainnet option */}
            <button
              onClick={() => onSwitch(false)}
              className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium transition-all rounded-lg mx-auto cursor-pointer ${!isTestnet
                ? 'text-[#22c55e] bg-[rgba(74,222,128,0.08)]'
                : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                }`}
            >
              <span className="w-2 h-2 rounded-full bg-[#22c55e] shrink-0" />
              Mainnet
              {!isTestnet && (
                <motion.svg
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-3.5 h-3.5 ml-auto text-[#22c55e]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </motion.svg>
              )}
            </button>
            {/* Testnet option */}
            <button
              onClick={() => onSwitch(true)}
              className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium transition-all rounded-lg mx-auto cursor-pointer ${isTestnet
                ? 'text-[#f59e0b] bg-[rgba(251,191,36,0.08)]'
                : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                }`}
            >
              <span className="w-2 h-2 rounded-full bg-[#f59e0b] shrink-0" />
              Testnet
              {isTestnet && (
                <motion.svg
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-3.5 h-3.5 ml-auto text-[#f59e0b]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </motion.svg>
              )}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const navLinks: NavItem[] = [
  {
    label: "Presales",
    items: [
      { href: "/presales", label: "Presale List" },
      { href: "/create-presale", label: "Create Presale" },
    ]
  },
  {
    label: "Tokens",
    items: [
      { href: "/tokens", label: "Token List" },
      { href: "/create-token", label: "Create Token" },
    ]
  },
  {
    label: "Lock",
    items: [
      { href: "/lock", label: "All Locks" },
      { href: "/create-lock", label: "Create Lock" },
    ]
  },
  { href: "/staking", label: "Staking" },
  {
    label: "Compliance",
    items: [
      { href: "/kyc", label: "KYC" },
      { href: "/audit", label: "Audit" },
    ]
  },
  {
    label: "Resources",
    items: [
      { href: "/docs", label: "Docs" },
      { href: "/blog", label: "Blog" },
      { href: "/whitepaper", label: "Whitepaper" },
    ]
  },
]

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { isConnected } = useWallet()

  const [mounted, setMounted] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  // Avoid hydration mismatch
  useEffect(() => {
    setMounted(true)
  }, [])

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMobileMenuOpen(false)
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [])

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [isMobileMenuOpen])

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${isScrolled
          ? "bg-background/80 backdrop-blur-xl dark:border-white/5 border-black/5 py-0 shadow-2xl"
          : "bg-transparent border-transparent py-2"
          }`}
      >
        <div className="container px-4 sm:px-6 mx-auto">
          <div className="flex h-14 sm:h-16 items-center justify-between gap-4">
            {/* Logo Wrapper - Left Wing */}
            <div className="flex-none sm:flex-1 flex items-center justify-start min-w-0">
              <Link href="/" className={`flex items-center gap-0 group relative logo-variant-charcoal scale-[0.8] sm:scale-90 origin-left transition-all duration-300 hover:brightness-110`}>
                <div className="logo-text-container drop-shadow-[0_0_15px_rgba(178,102,255,0.2)]">
                  <div className="flex items-center justify-center">
                    <span className="logo-text-t text-[52px] sm:text-[60px] logo-token leading-none">T</span>
                    <span className="logo-text-main text-[22px] sm:text-[26px] ml-0.5 logo-token whitespace-nowrap leading-none">oken</span>
                    <span className="logo-text-main text-[22px] sm:text-[26px] ml-2 logo-kickstarter whitespace-nowrap leading-none">Kickstarter</span>
                  </div>
                  <div className="logo-subtext text-[8px] sm:text-[9px] mt-[-10px] tracking-[0.25em] whitespace-nowrap opacity-90 text-center w-full">
                    <span className="logo-kickstart-your">Kickstart Your </span>
                    <span className="logo-journey">Journey</span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation - Centered Column */}
            <nav className="hidden xl:flex items-center justify-center grow shrink-0">
              <div className="flex items-center gap-0.5 2xl:gap-1 dark:bg-white/5 bg-black/5 px-2 py-1 rounded-full border dark:border-white/5 border-black/5">
                {navLinks.map((link, i) => (
                  link.items ? (
                    <NavDropdown key={link.label} label={link.label} items={link.items} />
                  ) : (
                    <motion.div
                      key={link.href}
                      className="relative group"
                      whileHover="hover"
                      initial="initial"
                    >
                      <Link
                        href={link.href!}
                        className="nav-link text-sm font-medium text-muted-foreground hover:text-foreground whitespace-nowrap px-4 py-2 rounded-full transition-all duration-300 flex items-center gap-1.5 hover:bg-white/5 active:scale-95"
                      >
                        {link.label}
                      </Link>
                      <motion.div
                        className="absolute inset-0 bg-primary/10 rounded-full -z-10 blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
                        layoutId="nav-hover"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    </motion.div>
                  )
                ))}
              </div>
            </nav>

            {/* Right Side Actions - Right Wing */}
            <div className="flex-none sm:flex-1 flex items-center justify-end gap-2 sm:gap-3 min-w-0">

              {/* Network Toggle - Desktop (hover dropdown) */}
              {mounted && (() => {
                const isTestnet = process.env.NEXT_PUBLIC_NETWORK_MODE === 'testnet'
                const handleNetworkSwitch = (targetIsTestnet: boolean) => {
                  if (targetIsTestnet && isTestnet) return
                  if (!targetIsTestnet && !isTestnet) return
                  window.location.href = targetIsTestnet
                    ? 'https://testnet.tokenkickstarter.com'
                    : 'https://tokenkickstarter.com'
                }
                return (
                  <NetworkDropdown isTestnet={isTestnet} onSwitch={handleNetworkSwitch} />
                )
              })()}

              <WalletButton />

              {/* Social Icons - Premium Styled */}
              <div className="hidden 2xl:flex items-center gap-2 ml-2 border-l dark:border-white/10 border-black/10 pl-3">
                <Link href="https://x.com/TokenKickstart" target="_blank" rel="noopener noreferrer" className="social-icon-btn group hover:!bg-foreground/20 hover:!text-foreground">
                  <svg className="w-4 h-4 transition-transform group-hover:rotate-12" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                </Link>
                <Link href="https://t.me/tokenkickstarter" target="_blank" rel="noopener noreferrer" className="social-icon-btn group hover:!bg-[#0088cc]/20 hover:!text-[#0088cc]">
                  <svg className="w-3.5 h-3.5 transition-transform group-hover:-rotate-12" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.11.02-1.93 1.23-5.46 3.62-.51.35-.98.52-1.4.51-.46-.01-1.35-.26-2.01-.48-.81-.27-1.45-.42-1.39-.88.03-.24.37-.48 1.02-.73 4-1.74 6.67-2.88 8.01-3.41 3.81-1.52 4.6-.1.01.01.95.14.7.14.45.14s1.63.15 1.63.53l-.01.3z" /></svg>
                </Link>
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="xl:hidden h-9 w-9 hover:bg-primary/10 rounded-full dark:hover:text-foreground ml-1"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {
          isMobileMenuOpen && (
            <>
              {/* Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm xl:hidden"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-hidden="true"
              />

              {/* Menu */}
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="fixed top-16 left-0 right-0 z-50 dark:bg-[#0a0a0f] bg-white border-b border-border/40 xl:hidden overflow-hidden"
                style={{ maxHeight: 'calc(100dvh - 4rem)' }}
              >
                <nav className="container py-4 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 5rem)' }}>
                  <div className="flex flex-col gap-1">
                    {navLinks.map((link, i) => (
                      <div key={link.label}>
                        {link.items ? (
                          <div className="flex flex-col">
                            <div className="px-4 py-3 text-base font-bold text-foreground">
                              {link.label}
                            </div>
                            <div className="flex flex-col pl-4 gap-1">
                              {link.items.map((item) => (
                                <Link
                                  key={item.href}
                                  href={item.href}
                                  className="flex items-center px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-xl transition-colors"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  {item.label}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <motion.div
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: i * 0.05 }}
                          >
                            <Link
                              href={link.href!}
                              className="flex items-center px-4 py-3 text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-xl transition-colors"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {link.label}
                            </Link>
                          </motion.div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Network Toggle - Mobile */}
                  {mounted && (() => {
                    const isTestnet = process.env.NEXT_PUBLIC_NETWORK_MODE === 'testnet'
                    const handleNetworkSwitch = (targetIsTestnet: boolean) => {
                      if (targetIsTestnet && isTestnet) return
                      if (!targetIsTestnet && !isTestnet) return
                      window.location.href = targetIsTestnet
                        ? 'https://testnet.tokenkickstarter.com'
                        : 'https://tokenkickstarter.com'
                    }
                    return (
                      <div className="mt-3 pt-3 border-t dark:border-white/10 border-black/10">
                        <div className="px-4 py-2 text-xs font-bold text-muted-foreground uppercase tracking-wider">Network</div>
                        <div className="flex gap-2 px-4 py-2 relative">
                          <motion.button
                            onClick={() => handleNetworkSwitch(false)}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors cursor-pointer ${!isTestnet
                              ? 'bg-[rgba(74,222,128,0.12)] text-[#22c55e] border border-[rgba(74,222,128,0.3)]'
                              : 'bg-muted/30 text-muted-foreground border border-transparent hover:bg-muted/50'
                              }`}
                            whileTap={isTestnet ? { scale: 0.97 } : {}}
                          >
                            <motion.span
                              className="w-2 h-2 rounded-full bg-[#22c55e]"
                              animate={!isTestnet ? { scale: [1, 1.4, 1] } : { scale: 1 }}
                              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            />
                            Mainnet
                          </motion.button>
                          <motion.button
                            onClick={() => handleNetworkSwitch(true)}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors cursor-pointer ${isTestnet
                              ? 'bg-[rgba(251,191,36,0.12)] text-[#f59e0b] border border-[rgba(251,191,36,0.3)]'
                              : 'bg-muted/30 text-muted-foreground border border-transparent hover:bg-muted/50'
                              }`}
                            whileTap={!isTestnet ? { scale: 0.97 } : {}}
                          >
                            <motion.span
                              className="w-2 h-2 rounded-full bg-[#f59e0b]"
                              animate={isTestnet ? { scale: [1, 1.4, 1] } : { scale: 1 }}
                              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            />
                            Testnet
                          </motion.button>
                        </div>
                      </div>
                    )
                  })()}


                </nav>
              </motion.div>
            </>
          )
        }
      </AnimatePresence >
    </>
  )
}

export default Header
